import React, { useEffect } from 'react';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useTranslation } from 'react-i18next';

interface ArticleItem {
  title: string;
  excerpt: string;
  date: string;
}

const Articles: React.FC = () => {
  const { t } = useTranslation();

  const articles = t('articles.items', { returnObjects: true }) as ArticleItem[];

  useEffect(() => {
    const handler = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const el = document.querySelector(target.getAttribute('href')!);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="articles-page">
      <style>{`
        .articles-page .section-shaped .shape-primary {
          background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important;
        }
        .articles-page .section-shaped .shape-primary :nth-child(1) { background: #3f78b7 !important; }
        .articles-page .section-shaped .shape-primary :nth-child(2) { background: #2e6aa8 !important; }
        .articles-page .section-shaped .shape-primary :nth-child(3) { background: #2a5b8e !important; }
        .articles-page .section-shaped .shape-primary :nth-child(4) { background: #1f6fb0 !important; }
        .articles-page .section-shaped .shape-primary :nth-child(5) { background: #0f4a86 !important; }
        .articles-page .section-shaped .shape-primary :nth-child(6) { background: #3f78b7 !important; }
        .articles-page .section-shaped .shape-primary :nth-child(7) { background: #2e6aa8 !important; }
        .articles-page .section-shaped .shape-primary :nth-child(8) { background: #2a5b8e !important; }
        .articles-page .section-shaped .shape-primary :nth-child(9) { background: #1f6fb0 !important; }
        .articles-page .section-shaped .shape-primary :nth-child(10) { background: #0f4a86 !important; }
        .articles-page .bg-gradient-primary {
          background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important;
        }

        .article-card {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
          overflow: hidden;
          transition: transform 0.25s, box-shadow 0.25s;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .article-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 28px rgba(0,0,0,0.12);
        }
        .article-card-img {
          height: 180px;
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .article-card-img i {
          font-size: 3rem;
          color: var(--primary);
          opacity: 0.6;
        }
        .article-card-body {
          padding: 1.5rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .article-card-date {
          font-size: 0.8rem;
          color: #8898aa;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .article-card-title {
          font-size: 1.15rem;
          font-weight: 600;
          color: #32325d;
          margin-bottom: 0.75rem;
        }
        .article-card-excerpt {
          font-size: 0.92rem;
          color: #525f7f;
          line-height: 1.6;
          flex: 1;
        }
        .article-card-link {
          display: inline-block;
          margin-top: 1rem;
          color: var(--primary);
          font-weight: 600;
          font-size: 0.9rem;
          text-decoration: none;
          transition: color 0.15s;
        }
        .article-card-link:hover {
          color: #1B5694;
          text-decoration: none;
        }
        .coming-soon-banner {
          text-align: center;
          padding: 2rem;
          color: #8898aa;
          font-size: 1.1rem;
          font-style: italic;
        }
      `}</style>

      <PublicNavbar />

      {/* Hero */}
      <section className="section section-hero section-shaped">
        <div className="shape shape-style-1 shape-primary">
          <span className="span-150"></span>
          <span className="span-50"></span>
          <span className="span-50"></span>
          <span className="span-75"></span>
          <span className="span-100"></span>
          <span className="span-75"></span>
          <span className="span-50"></span>
          <span className="span-100"></span>
          <span className="span-50"></span>
          <span className="span-100"></span>
        </div>
        <div className="page-header">
          <div className="container shape-container d-flex align-items-center py-lg">
            <div className="col px-0">
              <div className="row align-items-center justify-content-center">
                <div className="col-lg-8 text-center">
                  <h1 className="display-3 text-white mb-3">{t('articles.hero_title')}</h1>
                  <p className="lead text-white">{t('articles.hero_subtitle')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="separator separator-bottom separator-skew zindex-100">
          <svg x="0" y="0" viewBox="0 0 2560 100" preserveAspectRatio="none" version="1.1" xmlns="http://www.w3.org/2000/svg">
            <polygon className="fill-white" points="2560 0 2560 100 0 100"></polygon>
          </svg>
        </div>
      </section>

      {/* Article grid */}
      <section className="section">
        <div className="container">
          <div className="row">
            {Array.isArray(articles) && articles.map((article, idx) => (
              <div className="col-md-4 mb-4" key={idx}>
                <div className="article-card">
                  <div className="article-card-img">
                    <i className="ni ni-single-copy-04"></i>
                  </div>
                  <div className="article-card-body">
                    <div className="article-card-date">{formatDate(article.date)}</div>
                    <div className="article-card-title">{article.title}</div>
                    <p className="article-card-excerpt">{article.excerpt}</p>
                    <a className="article-card-link" href="#">
                      {t('articles.read_more')} &rarr;
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="coming-soon-banner">
            {t('articles.coming_soon')}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Articles;
